import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Delete, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import FolkSaptapurHeader from './FolkSaptapurHeader';
import FolkSaptapurTabs from './FolkSaptapurTabs';
import { folkSaptapurAPI } from '../../services/api';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function StudentPicker({ students, selected, onToggle, onSelectAll, onClear }) {
  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 0.5,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 170,
          overflowY: 'auto',
          mt: 1,
        }}
      >
        {students.map((s) => (
          <FormControlLabel
            key={s.student_id}
            control={<Checkbox size="small" checked={selected.includes(s.student_id)} onChange={() => onToggle(s.student_id)} />}
            label={s.name}
          />
        ))}
      </Box>
      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
        <Button size="small" onClick={onSelectAll}>Select All</Button>
        <Button size="small" onClick={onClear}>Clear</Button>
      </Box>
    </Box>
  );
}

function AssignServiceTasks() {
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [newStudentName, setNewStudentName] = useState('');

  const [weeklyTasks, setWeeklyTasks] = useState([]);
  const [weeklyText, setWeeklyText] = useState('');
  const [weeklyDays, setWeeklyDays] = useState([]);
  const [weeklyStudents, setWeeklyStudents] = useState([]);

  const [monthlyTasks, setMonthlyTasks] = useState([]);
  const [monthlyText, setMonthlyText] = useState('');
  const [monthlyStudents, setMonthlyStudents] = useState([]);

  const [specialText, setSpecialText] = useState('');
  const [specialDays, setSpecialDays] = useState([]);

  const loadAll = async () => {
    try {
      const [studentsRes, weeklyRes, monthlyRes, specialRes] = await Promise.all([
        folkSaptapurAPI.getStudents(),
        folkSaptapurAPI.listServiceTasks('weekly'),
        folkSaptapurAPI.listServiceTasks('monthly'),
        folkSaptapurAPI.getSpecialClass(),
      ]);
      setStudents(studentsRes.data.filter((s) => s.active));
      setWeeklyTasks(weeklyRes.data);
      setMonthlyTasks(monthlyRes.data);
      setSpecialText(specialRes.data.class_text);
      setSpecialDays(specialRes.data.class_days);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load');
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const flash = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleAddStudent = async () => {
    if (!newStudentName.trim()) {
      setError('Student name is required');
      return;
    }
    try {
      await folkSaptapurAPI.createStudent({ name: newStudentName.trim() });
      setNewStudentName('');
      flash('Student added');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add student');
    }
  };

  const handleMoveStudent = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= students.length) return;

    // Optimistic reorder - the same order this list shows is what
    // Morning Sadhana / Service Tasks / Sadhana Report will follow too.
    const reordered = [...students];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setStudents(reordered);

    try {
      await folkSaptapurAPI.reorderStudents(reordered.map((s) => s.student_id));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save order');
      loadAll(); // re-sync with the server's actual order
    }
  };

  const handleRemoveStudent = async (id, name) => {
    if (!window.confirm(`Remove student "${name}"? This clears them from every task list they were part of.`)) return;
    try {
      await folkSaptapurAPI.deleteStudent(id);
      flash('Student removed');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove student');
    }
  };

  const toggleDay = (days, setDays, day) => {
    setDays(days.includes(day) ? days.filter((d) => d !== day) : [...days, day]);
  };

  const handleAssignTask = async (category) => {
    setError('');
    const configs = {
      weekly: { text: weeklyText, students: weeklyStudents, extra: { task_days: weeklyDays.join(',') } },
      monthly: { text: monthlyText, students: monthlyStudents, extra: {} },
    };
    const cfg = configs[category];
    if (!cfg.text.trim()) {
      setError('Task text is required');
      return;
    }
    if (cfg.students.length === 0) {
      setError('Select at least one student');
      return;
    }
    if (category === 'weekly' && weeklyDays.length === 0) {
      setError('Pick at least one day of the week');
      return;
    }
    try {
      await folkSaptapurAPI.assignServiceTask(category, {
        task_text: cfg.text.trim(),
        student_ids: cfg.students,
        ...cfg.extra,
      });
      if (category === 'weekly') { setWeeklyText(''); setWeeklyDays([]); setWeeklyStudents([]); }
      if (category === 'monthly') { setMonthlyText(''); setMonthlyStudents([]); }
      flash('Task assigned');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign task');
    }
  };

  const handleRemoveTask = async (taskId) => {
    try {
      await folkSaptapurAPI.removeServiceTask(taskId);
      flash('Task removed');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove task');
    }
  };

  const handleSaveSpecialClass = async () => {
    if (!specialText.trim() || specialDays.length === 0) {
      setError('Special Class needs a description and at least one day');
      return;
    }
    try {
      await folkSaptapurAPI.saveSpecialClass({ class_text: specialText.trim(), class_days: specialDays.join(',') });
      flash('Special Class saved');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save Special Class');
    }
  };

  const studentNames = (ids) => ids.map((id) => students.find((s) => s.student_id === id)?.name).filter(Boolean).join(', ');

  return (
    <Box>
      <FolkSaptapurHeader />
      <FolkSaptapurTabs />
      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Manage Students */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Manage Students</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Students are their own roster here — nothing about them is linked to Employee records
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap', mb: 2 }}>
              <TextField label="Student Name" size="small" value={newStudentName} onChange={(e) => setNewStudentName(e.target.value)} sx={{ flex: 1, minWidth: 180 }} />
              <Button variant="contained" onClick={handleAddStudent}>+ Add Student</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.length === 0 ? (
                    <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>No students yet</TableCell></TableRow>
                  ) : students.map((s, idx) => (
                    <TableRow key={s.student_id} hover>
                      <TableCell>{s.name}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" disabled={idx === 0} onClick={() => handleMoveStudent(idx, -1)}>
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={idx === students.length - 1} onClick={() => handleMoveStudent(idx, 1)}>
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleRemoveStudent(s.student_id, s.name)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Tasks on Specific Day (formerly "Weekly Task") */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Tasks on Specific Day</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
              Recurs on one or two days of the week — assign to one or more students
            </Typography>
            <TextField fullWidth label="Task" size="small" value={weeklyText} onChange={(e) => setWeeklyText(e.target.value)} sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="textSecondary">Day(s) of week</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
              {DOW.map((d) => (
                <Chip
                  key={d}
                  label={d}
                  size="small"
                  color={weeklyDays.includes(d) ? 'secondary' : 'default'}
                  variant={weeklyDays.includes(d) ? 'filled' : 'outlined'}
                  onClick={() => toggleDay(weeklyDays, setWeeklyDays, d)}
                />
              ))}
            </Box>
            <StudentPicker
              students={students}
              selected={weeklyStudents}
              onToggle={(id) => setWeeklyStudents((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
              onSelectAll={() => setWeeklyStudents(students.map((s) => s.student_id))}
              onClear={() => setWeeklyStudents([])}
            />
            <Button variant="contained" size="small" sx={{ mt: 1.5 }} onClick={() => handleAssignTask('weekly')}>+ Assign Task</Button>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {weeklyTasks.map((t) => (
                <Box key={t.task_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{t.task_text} — {t.task_days.join(', ')}</Typography>
                    <Typography variant="caption" color="textSecondary">{studentNames(t.student_ids)}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveTask(t.task_id)}><Delete fontSize="small" /></IconButton>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Regular Tasks (formerly "Monthly Task") */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Regular Tasks</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
              Recurs every month — assign to one or more students
            </Typography>
            <TextField fullWidth label="Task" size="small" value={monthlyText} onChange={(e) => setMonthlyText(e.target.value)} sx={{ mb: 1.5 }} />
            <StudentPicker
              students={students}
              selected={monthlyStudents}
              onToggle={(id) => setMonthlyStudents((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
              onSelectAll={() => setMonthlyStudents(students.map((s) => s.student_id))}
              onClear={() => setMonthlyStudents([])}
            />
            <Button variant="contained" size="small" sx={{ mt: 1.5 }} onClick={() => handleAssignTask('monthly')}>+ Assign Task</Button>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {monthlyTasks.map((t) => (
                <Box key={t.task_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{t.task_text}</Typography>
                    <Typography variant="caption" color="textSecondary">{studentNames(t.student_ids)}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveTask(t.task_id)}><Delete fontSize="small" /></IconButton>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Special Class */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Special Class</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
              A recurring class-wide question, answered Yes/No only on its configured day(s) — applies to every
              student automatically, no student picker needed
            </Typography>
            <TextField fullWidth label="Description" size="small" value={specialText} onChange={(e) => setSpecialText(e.target.value)} sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="textSecondary">Day(s) of week</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, mb: 1.5 }}>
              {DOW.map((d) => (
                <Chip
                  key={d}
                  label={d}
                  size="small"
                  color={specialDays.includes(d) ? 'secondary' : 'default'}
                  variant={specialDays.includes(d) ? 'filled' : 'outlined'}
                  onClick={() => toggleDay(specialDays, setSpecialDays, d)}
                />
              ))}
            </Box>
            <Button variant="contained" size="small" onClick={handleSaveSpecialClass}>Save</Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default AssignServiceTasks;
